import { lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { StickyMobileCTA } from './components/ui/StickyMobileCTA';
import { ErrorBoundary } from './components/ErrorBoundary';
import { HandshakeModal } from './components/HandshakeModal';
import { PWAUpdatePrompt } from './components/PWAUpdatePrompt';
import { HomePage } from './pages/HomePage';
import { usePageTracking } from './hooks/useAnalytics';

// Lazy load all non-critical pages
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({ default: m.AboutPage })));
const ContactPage = lazy(() => import('./pages/ContactPage').then(m => ({ default: m.ContactPage })));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })));
const SecurityPolicyPage = lazy(() => import('./pages/SecurityPolicyPage'));
const SecurityAcknowledgmentsPage = lazy(() => import('./pages/SecurityAcknowledgmentsPage'));
const CybersecurityChecklistPage = lazy(() => import('./pages/CybersecurityChecklistPage').then(m => ({ default: m.CybersecurityChecklistPage })));
const PartnersPage = lazy(() => import('./pages/PartnersPage').then(m => ({ default: m.PartnersPage })));
const CommunityPage = lazy(() => import('./pages/CommunityPage').then(m => ({ default: m.CommunityPage })));
const FAQPage = lazy(() => import('./pages/FAQPage').then(m => ({ default: m.FAQPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));

// Service Pages - Lazy load
const AllInclusiveCare = lazy(() => import('./pages/services/AllInclusiveCare').then(m => ({ default: m.AllInclusiveCare })));
const ITSupportPage = lazy(() => import('./pages/services/ITSupportPage').then(m => ({ default: m.ITSupportPage })));
const EmergencyITSupportPage = lazy(() => import('./pages/services/EmergencyITSupportPage').then(m => ({ default: m.EmergencyITSupportPage })));
const RansomwareResponsePage = lazy(() => import('./pages/services/emergency/RansomwareResponsePage').then(m => ({ default: m.RansomwareResponsePage })));
const ServerDownPage = lazy(() => import('./pages/services/emergency/ServerDownPage').then(m => ({ default: m.ServerDownPage })));
const DataBreachPage = lazy(() => import('./pages/services/emergency/DataBreachPage').then(m => ({ default: m.DataBreachPage })));
const NetworkFailurePage = lazy(() => import('./pages/services/emergency/NetworkFailurePage').then(m => ({ default: m.NetworkFailurePage })));
const CybersecurityPage = lazy(() => import('./pages/services/CybersecurityPage').then(m => ({ default: m.CybersecurityPage })));
const Microsoft365Page = lazy(() => import('./pages/services/Microsoft365Page').then(m => ({ default: m.Microsoft365Page })));
const NetworkPage = lazy(() => import('./pages/services/NetworkPage').then(m => ({ default: m.NetworkPage })));
const NetworkSolutionsPage = lazy(() => import('./pages/services/NetworkSolutionsPage').then(m => ({ default: m.NetworkSolutionsPage })));
const ProjectsPage = lazy(() => import('./pages/services/ProjectsPage').then(m => ({ default: m.ProjectsPage })));
const SecurityCamerasPage = lazy(() => import('./pages/services/SecurityCamerasPage').then(m => ({ default: m.SecurityCamerasPage })));
const AccessControlPage = lazy(() => import('./pages/services/AccessControlPage').then(m => ({ default: m.AccessControlPage })));
const InstallationGuidePage = lazy(() => import('./pages/services/cameras/InstallationGuidePage').then(m => ({ default: m.InstallationGuidePage })));
const PoETechnicalGuidePage = lazy(() => import('./pages/services/cameras/PoETechnicalGuidePage').then(m => ({ default: m.PoETechnicalGuidePage })));
const CostsROIGuidePage = lazy(() => import('./pages/services/cameras/CostsROIGuidePage').then(m => ({ default: m.CostsROIGuidePage })));
const CablingPage = lazy(() => import('./pages/services/CablingPage').then(m => ({ default: m.CablingPage })));
const ComputerRepairPage = lazy(() => import('./pages/services/ComputerRepairPage').then(m => ({ default: m.ComputerRepairPage })));
const NetworkCablingContractorsPage = lazy(() => import('./pages/services/NetworkCablingContractorsPage').then(m => ({ default: m.NetworkCablingContractorsPage })));
const VoIPPhoneSystemsPage = lazy(() => import('./pages/services/VoIPPhoneSystemsPage').then(m => ({ default: m.VoIPPhoneSystemsPage })));
const CloudMigrationPage = lazy(() => import('./pages/services/CloudMigrationPage').then(m => ({ default: m.CloudMigrationPage })));
const SoundMaskingPagingPage = lazy(() => import('./pages/services/SoundMaskingPagingPage').then(m => ({ default: m.SoundMaskingPagingPage })));
const DataRecoveryPage = lazy(() => import('./pages/services/DataRecoveryPage').then(m => ({ default: m.DataRecoveryPage })));
const WebServicesPage = lazy(() => import('./pages/services/WebServicesPage').then(m => ({ default: m.WebServicesPage })));
const ManagedNetworkPage = lazy(() => import('./pages/ManagedNetworkPage').then(m => ({ default: m.ManagedNetworkPage })));
const SecureStartupPackages = lazy(() => import('./pages/services/SecureStartupPackages').then(m => ({ default: m.SecureStartupPackages })));
const TVMountingPage = lazy(() => import('./pages/services/TVMountingPage').then(m => ({ default: m.TVMountingPage })));
const UbiquitiUniFiPage = lazy(() => import('./pages/services/UbiquitiUniFiPage').then(m => ({ default: m.UbiquitiUniFiPage })));
const EeroProInstallerPage = lazy(() => import('./pages/services/EeroProInstallerPage').then(m => ({ default: m.EeroProInstallerPage })));
const ServicesIndexPage = lazy(() => import('./pages/services/ServicesIndexPage').then(m => ({ default: m.ServicesIndexPage })));
const Microsoft365SecurityPage = lazy(() => import('./pages/services/Microsoft365SecurityPage').then(m => ({ default: m.Microsoft365SecurityPage })));
const BoiseStructuredCablingPage = lazy(() => import('./pages/location/BoiseStructuredCablingPage').then(m => ({ default: m.BoiseStructuredCablingPage })));
const BoiseCybersecurityPage = lazy(() => import('./pages/location/BoiseCybersecurityPage').then(m => ({ default: m.BoiseCybersecurityPage })));
const MeridianLowVoltageContractorPage = lazy(() => import('./pages/location/MeridianLowVoltageContractorPage').then(m => ({ default: m.MeridianLowVoltageContractorPage })));
const MeridianManagedCybersecurityPage = lazy(() => import('./pages/location/MeridianManagedCybersecurityPage').then(m => ({ default: m.MeridianManagedCybersecurityPage })));

// UniFi Service Pages - Lazy load
const UniFiInstallationPage = lazy(() => import('./pages/services/UniFiInstallationPage').then(m => ({ default: m.UniFiInstallationPage })));
const UniFiServicesHubPage = lazy(() => import('./pages/services/UniFiServicesHubPage').then(m => ({ default: m.UniFiServicesHubPage })));
const StarlinkInstallationPage = lazy(() => import('./pages/services/StarlinkInstallationPage').then(m => ({ default: m.StarlinkInstallationPage })));
const UniFiVsMerakiPage = lazy(() => import('./pages/services/UniFiVsMerakiPage').then(m => ({ default: m.UniFiVsMerakiPage })));

// Transparency Pages - Lazy load
const PricingRangesPage = lazy(() => import('./pages/PricingRangesPage').then(m => ({ default: m.PricingRangesPage })));
const SLAPage = lazy(() => import('./pages/SLAPage').then(m => ({ default: m.SLAPage })));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage').then(m => ({ default: m.OnboardingPage })));

// Admin Pages - Lazy load
const PageBuilder = lazy(() => import('./pages/admin/PageBuilder').then(m => ({ default: m.PageBuilder })));

// Location Pages - Lazy load
const BoisePage = lazy(() => import('./pages/location/BoisePage').then(m => ({ default: m.BoisePage })));
const MeridianPage = lazy(() => import('./pages/location/MeridianPage').then(m => ({ default: m.MeridianPage })));
const NampaPage = lazy(() => import('./pages/location/NampaPage').then(m => ({ default: m.NampaPage })));
const EaglePage = lazy(() => import('./pages/location/EaglePage').then(m => ({ default: m.EaglePage })));
const CaldwellPage = lazy(() => import('./pages/location/CaldwellPage').then(m => ({ default: m.CaldwellPage })));
const KunaPage = lazy(() => import('./pages/location/KunaPage').then(m => ({ default: m.KunaPage })));
const StarPage = lazy(() => import('./pages/location/StarPage').then(m => ({ default: m.StarPage })));
const GardenCityPage = lazy(() => import('./pages/location/GardenCityPage').then(m => ({ default: m.GardenCityPage })));
const MiddletonPage = lazy(() => import('./pages/location/MiddletonPage').then(m => ({ default: m.MiddletonPage })));
const SouthMeridianPage = lazy(() => import('./pages/location/SouthMeridianPage').then(m => ({ default: m.SouthMeridianPage })));
const SEBoisePage = lazy(() => import('./pages/location/SEBoisePage').then(m => ({ default: m.SEBoisePage })));

// Network Cabling & Low Voltage Service Pages - Lazy load
const DataCablingPage = lazy(() => import('./pages/services/DataCablingPage').then(m => ({ default: m.DataCablingPage })));
const StructuredCablingPage = lazy(() => import('./pages/services/StructuredCablingPage').then(m => ({ default: m.StructuredCablingPage })));
const LowVoltageCablingPage = lazy(() => import('./pages/services/LowVoltageCablingPage').then(m => ({ default: m.LowVoltageCablingPage })));
const OfficeCablingPage = lazy(() => import('./pages/services/OfficeCablingPage').then(m => ({ default: m.OfficeCablingPage })));
const FiberCablingPage = lazy(() => import('./pages/services/FiberCablingPage').then(m => ({ default: m.FiberCablingPage })));
const VoiceDataCablingPage = lazy(() => import('./pages/services/VoiceDataCablingPage').then(m => ({ default: m.VoiceDataCablingPage })));
const NetworkRackInstallationPage = lazy(() => import('./pages/services/NetworkRackInstallationPage').then(m => ({ default: m.NetworkRackInstallationPage })));

// City-specific Network Cabling Pages - Lazy load
const BoiseNetworkCablingPage = lazy(() => import('./pages/location/BoiseNetworkCablingPage').then(m => ({ default: m.BoiseNetworkCablingPage })));
const MeridianNetworkCablingPage = lazy(() => import('./pages/location/MeridianNetworkCablingPage').then(m => ({ default: m.MeridianNetworkCablingPage })));
const NampaNetworkCablingPage = lazy(() => import('./pages/location/NampaNetworkCablingPage').then(m => ({ default: m.NampaNetworkCablingPage })));
const EagleNetworkCablingPage = lazy(() => import('./pages/location/EagleNetworkCablingPage').then(m => ({ default: m.EagleNetworkCablingPage })));
const CaldwellNetworkCablingPage = lazy(() => import('./pages/location/CaldwellNetworkCablingPage').then(m => ({ default: m.CaldwellNetworkCablingPage })));
const KunaNetworkCablingPage = lazy(() => import('./pages/location/KunaNetworkCablingPage').then(m => ({ default: m.KunaNetworkCablingPage })));
const StarNetworkCablingPage = lazy(() => import('./pages/location/StarNetworkCablingPage').then(m => ({ default: m.StarNetworkCablingPage })));
const GardenCityNetworkCablingPage = lazy(() => import('./pages/location/GardenCityNetworkCablingPage').then(m => ({ default: m.GardenCityNetworkCablingPage })));
const MiddletonNetworkCablingPage = lazy(() => import('./pages/location/MiddletonNetworkCablingPage').then(m => ({ default: m.MiddletonNetworkCablingPage })));

// City-specific UniFi Pages - Lazy load
const BoiseUniFiInstallerPage = lazy(() => import('./pages/location/BoiseUniFiInstallerPage').then(m => ({ default: m.BoiseUniFiInstallerPage })));
const MeridianUniFiInstallerPage = lazy(() => import('./pages/location/MeridianUniFiInstallerPage').then(m => ({ default: m.MeridianUniFiInstallerPage })));
const NampaUniFiInstallerPage = lazy(() => import('./pages/location/NampaUniFiInstallerPage').then(m => ({ default: m.NampaUniFiInstallerPage })));
const EagleUniFiInstallerPage = lazy(() => import('./pages/location/EagleUniFiInstallerPage').then(m => ({ default: m.EagleUniFiInstallerPage })));
const CaldwellUniFiInstallerPage = lazy(() => import('./pages/location/CaldwellUniFiInstallerPage').then(m => ({ default: m.CaldwellUniFiInstallerPage })));
const KunaUniFiInstallerPage = lazy(() => import('./pages/location/KunaUniFiInstallerPage').then(m => ({ default: m.KunaUniFiInstallerPage })));
const StarUniFiInstallerPage = lazy(() => import('./pages/location/StarUniFiInstallerPage').then(m => ({ default: m.StarUniFiInstallerPage })));
const GardenCityUniFiInstallerPage = lazy(() => import('./pages/location/GardenCityUniFiInstallerPage').then(m => ({ default: m.GardenCityUniFiInstallerPage })));
const MiddletonUniFiInstallerPage = lazy(() => import('./pages/location/MiddletonUniFiInstallerPage').then(m => ({ default: m.MiddletonUniFiInstallerPage })));

// Blog Pages - Lazy load
const BlogIndex = lazy(() => import('./pages/blog/BlogIndex').then(m => ({ default: m.BlogIndex })));
const BlogPostLoader = lazy(() => import('./pages/blog/BlogPostLoader').then(m => ({ default: m.BlogPostLoader })));

// Blog Category Pages - Lazy load for performance
const BlogNetworkInfrastructurePage = lazy(() => import('./pages/blog/category/NetworkInfrastructurePage').then(m => ({ default: m.NetworkInfrastructurePage })));
const BlogConnectivityPage = lazy(() => import('./pages/blog/category/ConnectivityPage').then(m => ({ default: m.ConnectivityPage })));
const BlogCybersecurityPage = lazy(() => import('./pages/blog/category/CybersecurityPage').then(m => ({ default: m.CybersecurityPage })));
const BlogITSupportPage = lazy(() => import('./pages/blog/category/ITSupportPage').then(m => ({ default: m.ITSupportPage })));
const BlogUniFiSolutionsPage = lazy(() => import('./pages/blog/category/UniFiSolutionsPage').then(m => ({ default: m.UniFiSolutionsPage })));
const BlogMicrosoft365Page = lazy(() => import('./pages/blog/category/Microsoft365Page').then(m => ({ default: m.Microsoft365Page })));
const BlogSecuritySystemsPage = lazy(() => import('./pages/blog/category/SecuritySystemsPage').then(m => ({ default: m.SecuritySystemsPage })));
const BlogTreasureValleyTechUpdatePage = lazy(() => import('./pages/blog/category/TreasureValleyTechUpdatePage').then(m => ({ default: m.TreasureValleyTechUpdatePage })));
const BlogBusinessAutomationPage = lazy(() => import('./pages/blog/category/BusinessAutomationPage').then(m => ({ default: m.BusinessAutomationPage })));

// Loading component for lazy loaded routes
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

function App() {
  // Track page views automatically when route changes
  usePageTracking();

  const location = useLocation();

  // Determine modal variant based on current page
  const getModalVariant = () => {
    const path = location.pathname;

    if (path.includes('/services/cabling') || path.includes('/services/network-cabling')) {
      return 'cabling';
    } else if (path.includes('/services/microsoft-365') || path.includes('/blog/category/microsoft-365')) {
      return 'm365';
    } else if (path.includes('/services/all-inclusive-care')) {
      return 'general'; // Could add a specific variant for all-inclusive-care if needed
    }

    return 'general';
  };

  // Determine if modal should be enabled (disable on contact and certain pages)
  const isModalEnabled = () => {
    const path = location.pathname;
    const disabledPaths = ['/contact', '/privacy', '/terms', '/security'];
    return !disabledPaths.some(disabledPath => path.startsWith(disabledPath));
  };

  return (
    <div className="min-h-screen flex flex-col">
      <PWAUpdatePrompt />
      <Header />
      <main className="flex-grow">
        <ErrorBoundary>
          <Routes>
            {/* Main Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={
              <Suspense fallback={<PageLoader />}>
                <AboutPage />
              </Suspense>
            } />
            <Route path="/contact" element={
              <Suspense fallback={<PageLoader />}>
                <ContactPage />
              </Suspense>
            } />
            <Route path="/privacy" element={
              <Suspense fallback={<PageLoader />}>
                <PrivacyPage />
              </Suspense>
            } />
            <Route path="/terms" element={
              <Suspense fallback={<PageLoader />}>
                <TermsPage />
              </Suspense>
            } />
            <Route path="/security" element={
              <Suspense fallback={<PageLoader />}>
                <SecurityPolicyPage />
              </Suspense>
            } />
            <Route path="/security/acknowledgments" element={
              <Suspense fallback={<PageLoader />}>
                <SecurityAcknowledgmentsPage />
              </Suspense>
            } />
            <Route path="/cybersecurity-checklist" element={
              <Suspense fallback={<PageLoader />}>
                <CybersecurityChecklistPage />
              </Suspense>
            } />
            <Route path="/partners" element={
              <Suspense fallback={<PageLoader />}>
                <PartnersPage />
              </Suspense>
            } />
            <Route path="/community" element={
              <Suspense fallback={<PageLoader />}>
                <CommunityPage />
              </Suspense>
            } />
            <Route path="/faq" element={
              <Suspense fallback={<PageLoader />}>
                <FAQPage />
              </Suspense>
            } />

            {/* Transparency Pages */}
            <Route path="/pricing-ranges" element={
              <Suspense fallback={<PageLoader />}>
                <PricingRangesPage />
              </Suspense>
            } />
            <Route path="/sla" element={
              <Suspense fallback={<PageLoader />}>
                <SLAPage />
              </Suspense>
            } />
            <Route path="/onboarding" element={
              <Suspense fallback={<PageLoader />}>
                <OnboardingPage />
              </Suspense>
            } />

            {/* Service Routes */}
            <Route path="/services" element={
              <Suspense fallback={<PageLoader />}>
                <ServicesIndexPage />
              </Suspense>
            } />
            <Route path="/services/all-inclusive-care" element={
              <Suspense fallback={<PageLoader />}>
                <AllInclusiveCare />
              </Suspense>
            } />
            <Route path="/services/it-support" element={
              <Suspense fallback={<PageLoader />}>
                <ITSupportPage />
              </Suspense>
            } />
            <Route path="/services/emergency-it-support" element={
              <Suspense fallback={<PageLoader />}>
                <EmergencyITSupportPage />
              </Suspense>
            } />
            <Route path="/services/emergency/ransomware-response" element={
              <Suspense fallback={<PageLoader />}>
                <RansomwareResponsePage />
              </Suspense>
            } />
            <Route path="/services/emergency/server-down" element={
              <Suspense fallback={<PageLoader />}>
                <ServerDownPage />
              </Suspense>
            } />
            <Route path="/services/emergency/data-breach" element={
              <Suspense fallback={<PageLoader />}>
                <DataBreachPage />
              </Suspense>
            } />
            <Route path="/services/emergency/network-failure" element={
              <Suspense fallback={<PageLoader />}>
                <NetworkFailurePage />
              </Suspense>
            } />
            <Route path="/services/cybersecurity" element={
              <Suspense fallback={<PageLoader />}>
                <CybersecurityPage />
              </Suspense>
            } />
            <Route path="/services/microsoft-365" element={
              <Suspense fallback={<PageLoader />}>
                <Microsoft365Page />
              </Suspense>
            } />
            <Route path="/services/microsoft-365-security" element={
              <Suspense fallback={<PageLoader />}>
                <Microsoft365SecurityPage />
              </Suspense>
            } />
            <Route path="/services/network" element={
              <Suspense fallback={<PageLoader />}>
                <NetworkPage />
              </Suspense>
            } />
            <Route path="/services/network-solutions" element={
              <Suspense fallback={<PageLoader />}>
                <NetworkSolutionsPage />
              </Suspense>
            } />
            <Route path="/services/projects" element={
              <Suspense fallback={<PageLoader />}>
                <ProjectsPage />
              </Suspense>
            } />
            <Route path="/services/security-cameras" element={
              <Suspense fallback={<PageLoader />}>
                <SecurityCamerasPage />
              </Suspense>
            } />
            <Route path="/services/access-control" element={
              <Suspense fallback={<PageLoader />}>
                <AccessControlPage />
              </Suspense>
            } />
            <Route path="/services/cameras/installation-guide" element={
              <Suspense fallback={<PageLoader />}>
                <InstallationGuidePage />
              </Suspense>
            } />
            <Route path="/services/cameras/poe-technical-guide" element={
              <Suspense fallback={<PageLoader />}>
                <PoETechnicalGuidePage />
              </Suspense>
            } />
            <Route path="/services/cameras/costs-roi-guide" element={
              <Suspense fallback={<PageLoader />}>
                <CostsROIGuidePage />
              </Suspense>
            } />
            <Route path="/services/cabling" element={
              <Suspense fallback={<PageLoader />}>
                <CablingPage />
              </Suspense>
            } />
            <Route path="/services/computer-repair" element={
              <Suspense fallback={<PageLoader />}>
                <ComputerRepairPage />
              </Suspense>
            } />
            <Route path="/services/network-cabling-contractors" element={
              <Suspense fallback={<PageLoader />}>
                <NetworkCablingContractorsPage />
              </Suspense>
            } />

            {/* Network Cabling & Low Voltage Service Pages */}
            <Route path="/services/data-cabling" element={
              <Suspense fallback={<PageLoader />}>
                <DataCablingPage />
              </Suspense>
            } />
            <Route path="/services/structured-cabling" element={
              <Suspense fallback={<PageLoader />}>
                <StructuredCablingPage />
              </Suspense>
            } />
            <Route path="/services/low-voltage-cabling" element={
              <Suspense fallback={<PageLoader />}>
                <LowVoltageCablingPage />
              </Suspense>
            } />
            <Route path="/services/office-cabling" element={
              <Suspense fallback={<PageLoader />}>
                <OfficeCablingPage />
              </Suspense>
            } />
            <Route path="/services/fiber-cabling" element={
              <Suspense fallback={<PageLoader />}>
                <FiberCablingPage />
              </Suspense>
            } />
            <Route path="/services/voice-and-data-cabling" element={
              <Suspense fallback={<PageLoader />}>
                <VoiceDataCablingPage />
              </Suspense>
            } />
            <Route path="/services/network-rack-installation" element={
              <Suspense fallback={<PageLoader />}>
                <NetworkRackInstallationPage />
              </Suspense>
            } />

            <Route path="/services/voip-phone-systems" element={
              <Suspense fallback={<PageLoader />}>
                <VoIPPhoneSystemsPage />
              </Suspense>
            } />
            <Route path="/services/cloud-migration" element={
              <Suspense fallback={<PageLoader />}>
                <CloudMigrationPage />
              </Suspense>
            } />
            <Route path="/services/sound-masking-paging" element={
              <Suspense fallback={<PageLoader />}>
                <SoundMaskingPagingPage />
              </Suspense>
            } />
            <Route path="/services/data-recovery" element={
              <Suspense fallback={<PageLoader />}>
                <DataRecoveryPage />
              </Suspense>
            } />
            <Route path="/services/web-services" element={
              <Suspense fallback={<PageLoader />}>
                <WebServicesPage />
              </Suspense>
            } />
            <Route path="/managed-network" element={
              <Suspense fallback={<PageLoader />}>
                <ManagedNetworkPage />
              </Suspense>
            } />
            <Route path="/services/secure-startup-packages" element={
              <Suspense fallback={<PageLoader />}>
                <SecureStartupPackages />
              </Suspense>
            } />
            <Route path="/services/tv-mounting" element={
              <Suspense fallback={<PageLoader />}>
                <TVMountingPage />
              </Suspense>
            } />
            <Route path="/ubiquiti-networks-support" element={
              <Suspense fallback={<PageLoader />}>
                <UbiquitiUniFiPage />
              </Suspense>
            } />
            <Route path="/eero-pro-installer-meridian-id" element={
              <Suspense fallback={<PageLoader />}>
                <EeroProInstallerPage />
              </Suspense>
            } />
            <Route path="/services/unifi-installation" element={
              <Suspense fallback={<PageLoader />}>
                <UniFiInstallationPage />
              </Suspense>
            } />
            <Route path="/services/unifi" element={
              <Suspense fallback={<PageLoader />}>
                <UniFiServicesHubPage />
              </Suspense>
            } />
            <Route path="/unifi-vs-meraki" element={
              <Suspense fallback={<PageLoader />}>
                <UniFiVsMerakiPage />
              </Suspense>
            } />
            <Route path="/services/starlink-installation" element={
              <Suspense fallback={<PageLoader />}>
                <StarlinkInstallationPage />
              </Suspense>
            } />

            {/* Admin Routes */}
            <Route path="/admin/page-builder" element={
              <Suspense fallback={<PageLoader />}>
                <PageBuilder />
              </Suspense>
            } />

            {/* Location Routes */}
            <Route path="/location/boise" element={
              <Suspense fallback={<PageLoader />}>
                <BoisePage />
              </Suspense>
            } />
            <Route path="/location/meridian" element={
              <Suspense fallback={<PageLoader />}>
                <MeridianPage />
              </Suspense>
            } />
            <Route path="/location/nampa" element={
              <Suspense fallback={<PageLoader />}>
                <NampaPage />
              </Suspense>
            } />
            <Route path="/location/eagle" element={
              <Suspense fallback={<PageLoader />}>
                <EaglePage />
              </Suspense>
            } />
            <Route path="/location/caldwell" element={
              <Suspense fallback={<PageLoader />}>
                <CaldwellPage />
              </Suspense>
            } />
            <Route path="/location/kuna" element={
              <Suspense fallback={<PageLoader />}>
                <KunaPage />
              </Suspense>
            } />
            <Route path="/location/star" element={
              <Suspense fallback={<PageLoader />}>
                <StarPage />
              </Suspense>
            } />
            <Route path="/location/garden-city" element={
              <Suspense fallback={<PageLoader />}>
                <GardenCityPage />
              </Suspense>
            } />
            <Route path="/location/middleton" element={
              <Suspense fallback={<PageLoader />}>
                <MiddletonPage />
              </Suspense>
            } />
            <Route path="/locations/south-meridian-network-cabling-managed-it" element={
              <Suspense fallback={<PageLoader />}>
                <SouthMeridianPage />
              </Suspense>
            } />
            <Route path="/locations/se-boise-business-wifi-voip" element={
              <Suspense fallback={<PageLoader />}>
                <SEBoisePage />
              </Suspense>
            } />
            <Route path="/locations/boise-structured-cabling" element={
              <Suspense fallback={<PageLoader />}>
                <BoiseStructuredCablingPage />
              </Suspense>
            } />
            <Route path="/locations/boise-cybersecurity" element={
              <Suspense fallback={<PageLoader />}>
                <BoiseCybersecurityPage />
              </Suspense>
            } />
            <Route path="/locations/meridian-low-voltage-contractor" element={
              <Suspense fallback={<PageLoader />}>
                <MeridianLowVoltageContractorPage />
              </Suspense>
            } />
            <Route path="/locations/meridian-managed-cybersecurity" element={
              <Suspense fallback={<PageLoader />}>
                <MeridianManagedCybersecurityPage />
              </Suspense>
            } />

            {/* City-specific Network Cabling Routes */}
            <Route path="/location/boise/network-cabling" element={
              <Suspense fallback={<PageLoader />}>
                <BoiseNetworkCablingPage />
              </Suspense>
            } />
            <Route path="/location/meridian/network-cabling" element={
              <Suspense fallback={<PageLoader />}>
                <MeridianNetworkCablingPage />
              </Suspense>
            } />
            <Route path="/location/nampa/network-cabling" element={
              <Suspense fallback={<PageLoader />}>
                <NampaNetworkCablingPage />
              </Suspense>
            } />
            <Route path="/location/eagle/network-cabling" element={
              <Suspense fallback={<PageLoader />}>
                <EagleNetworkCablingPage />
              </Suspense>
            } />
            <Route path="/location/caldwell/network-cabling" element={
              <Suspense fallback={<PageLoader />}>
                <CaldwellNetworkCablingPage />
              </Suspense>
            } />
            <Route path="/location/kuna/network-cabling" element={
              <Suspense fallback={<PageLoader />}>
                <KunaNetworkCablingPage />
              </Suspense>
            } />
            <Route path="/location/star/network-cabling" element={
              <Suspense fallback={<PageLoader />}>
                <StarNetworkCablingPage />
              </Suspense>
            } />
            <Route path="/location/garden-city/network-cabling" element={
              <Suspense fallback={<PageLoader />}>
                <GardenCityNetworkCablingPage />
              </Suspense>
            } />
            <Route path="/location/middleton/network-cabling" element={
              <Suspense fallback={<PageLoader />}>
                <MiddletonNetworkCablingPage />
              </Suspense>
            } />

            {/* City-Specific UniFi Routes */}
            <Route path="/location/boise/unifi-installer" element={
              <Suspense fallback={<PageLoader />}>
                <BoiseUniFiInstallerPage />
              </Suspense>
            } />
            <Route path="/location/meridian/unifi-installer" element={
              <Suspense fallback={<PageLoader />}>
                <MeridianUniFiInstallerPage />
              </Suspense>
            } />
            <Route path="/location/nampa/unifi-installer" element={
              <Suspense fallback={<PageLoader />}>
                <NampaUniFiInstallerPage />
              </Suspense>
            } />
            <Route path="/location/eagle/unifi-installer" element={
              <Suspense fallback={<PageLoader />}>
                <EagleUniFiInstallerPage />
              </Suspense>
            } />
            <Route path="/location/caldwell/unifi-installer" element={
              <Suspense fallback={<PageLoader />}>
                <CaldwellUniFiInstallerPage />
              </Suspense>
            } />
            <Route path="/location/kuna/unifi-installer" element={
              <Suspense fallback={<PageLoader />}>
                <KunaUniFiInstallerPage />
              </Suspense>
            } />
            <Route path="/location/star/unifi-installer" element={
              <Suspense fallback={<PageLoader />}>
                <StarUniFiInstallerPage />
              </Suspense>
            } />
            <Route path="/location/garden-city/unifi-installer" element={
              <Suspense fallback={<PageLoader />}>
                <GardenCityUniFiInstallerPage />
              </Suspense>
            } />
            <Route path="/location/middleton/unifi-installer" element={
              <Suspense fallback={<PageLoader />}>
                <MiddletonUniFiInstallerPage />
              </Suspense>
            } />

            {/* Blog Routes */}
            <Route path="/blog" element={
              <Suspense fallback={<PageLoader />}>
                <BlogIndex />
              </Suspense>
            } />

            {/* Dynamic Blog Post Route - Catches all blog posts */}
            <Route path="/blog/:slug" element={
              <Suspense fallback={<PageLoader />}>
                <BlogPostLoader />
              </Suspense>
            } />

            {/* Blog Category Routes */}
            <Route path="/blog/category/network-infrastructure" element={
              <Suspense fallback={<PageLoader />}>
                <BlogNetworkInfrastructurePage />
              </Suspense>
            } />
            <Route path="/blog/category/connectivity" element={
              <Suspense fallback={<PageLoader />}>
                <BlogConnectivityPage />
              </Suspense>
            } />
            <Route path="/blog/category/cybersecurity" element={
              <Suspense fallback={<PageLoader />}>
                <BlogCybersecurityPage />
              </Suspense>
            } />
            <Route path="/blog/category/it-support" element={
              <Suspense fallback={<PageLoader />}>
                <BlogITSupportPage />
              </Suspense>
            } />
            <Route path="/blog/category/unifi-solutions" element={
              <Suspense fallback={<PageLoader />}>
                <BlogUniFiSolutionsPage />
              </Suspense>
            } />
            <Route path="/blog/category/microsoft-365" element={
              <Suspense fallback={<PageLoader />}>
                <BlogMicrosoft365Page />
              </Suspense>
            } />
            <Route path="/blog/category/security-systems" element={
              <Suspense fallback={<PageLoader />}>
                <BlogSecuritySystemsPage />
              </Suspense>
            } />
            <Route path="/blog/category/treasure-valley-tech-update" element={
              <Suspense fallback={<PageLoader />}>
                <BlogTreasureValleyTechUpdatePage />
              </Suspense>
            } />
            <Route path="/blog/category/business-automation" element={
              <Suspense fallback={<PageLoader />}>
                <BlogBusinessAutomationPage />
              </Suspense>
            } />

            {/* 404 Catch-all route */}
            <Route path="*" element={
              <Suspense fallback={<PageLoader />}>
                <NotFoundPage />
              </Suspense>
            } />
          </Routes>
        </ErrorBoundary>
      </main>
      <Footer />
      <StickyMobileCTA />

      {/* Handshake Modal — disabled, using chat widget for lead capture */}
      {/* <HandshakeModal
        variant={getModalVariant() as 'general' | 'cabling' | 'm365'}
        showExitIntent={isModalEnabled()}
        showTimeDelay={isModalEnabled()}
        initialDelayMs={30000}
        onSubmitSuccess={() => console.log('Lead captured successfully!')}
      /> */}
    </div>
  );
}

export default App;